import { useMutation } from '@tanstack/react-query'
import { toast } from 'sonner'
import useAuth from '@/features/auth/hooks/use-auth'
import { handleMutationError } from '@/shared/utils/handle-api-error'
import { portalAdminV2Service } from '../services/portal-admin-v2.service'

/**
 * **Compartir el enlace del portal por fuera del correo** (decisión Nº 48).
 *
 * ── ☠️ POR QUÉ ESTO ES UNA MUTACIÓN Y NO UNA QUERY ──────────────────────────
 *
 * Lo que devuelve es la credencial que abre el KYC del cliente **sin autenticar**. Una `useQuery`
 * la guardaría en el cache de react-query, donde queda después de que la pantalla se fue y de donde
 * la levanta cualquier devtool abierta. Una mutación se dispara, entrega y se olvida.
 *
 * Por eso también **el valor no vuelve al que llama**: entra al portapapeles acá adentro y la
 * función resuelve `void`. Un componente que lo recibiera lo pondría en un `useState`, y de ahí a
 * un `<input value={url}>` para «que el asesor lo vea» hay un solo commit — y eso lo deja en
 * capturas de pantalla, en dumps del DOM y en el session replay de Sentry.
 *
 * El toast dice «Enlace copiado». No dice el enlace.
 *
 * ── POR QUÉ UN HOOK COMPARTIDO Y NO EL `clipboard` EN CADA PANTALLA ─────────
 *
 * Porque son CUATRO superficies —la tabla de flujos, el panel de detalle, el panel del flujo activo
 * del cliente y la pantalla de revisión—, y en v1 las cuatro repetían el mismo
 * `navigator.clipboard.writeText(...)` armando la URL a mano. Cuatro copias de una regla de
 * seguridad son cuatro lugares donde aflojarla sin que nadie se entere.
 */
export function usePortalLink() {
  const { auth } = useAuth()
  const accessToken = auth?.v2AccessToken ?? ''

  const copiar = async (url: string) => {
    await navigator.clipboard.writeText(url)
    toast.success('Enlace copiado', {
      // La fecha SÍ se puede mostrar: no abre nada. Y es lo que el asesor necesita saber antes de
      // pegarlo en un chat, en vez de enterarse cuando el cliente le dice que no funciona.
      description: 'Pegalo donde lo vayas a compartir.'
    })
  }

  const reveal = useMutation({
    mutationFn: async (id: string) => await portalAdminV2Service.revealLink(accessToken, id),
    onSuccess: async (link) => {
      await copiar(link.url)
    },
    onError: handleMutationError
  })

  const rotate = useMutation({
    mutationFn: async (id: string) => await portalAdminV2Service.rotateLink(accessToken, id),
    onSuccess: async (link) => {
      await copiar(link.url)
      toast.warning('El enlace anterior dejó de funcionar', {
        description: 'Si ya se lo habías enviado al cliente, vuelve a enviárselo.'
      })
    },
    onError: handleMutationError
  })

  return {
    /** Copia el enlace vigente al portapapeles. No devuelve la URL: ver el comentario de arriba. */
    copyLink: (id: string) => {
      reveal.mutate(id)
    },
    /** Acuña uno nuevo, mata el anterior y copia el nuevo. */
    rotateLink: (id: string) => {
      rotate.mutate(id)
    },
    isCopying: reveal.isPending,
    isRotating: rotate.isPending,
    isReady: Boolean(accessToken)
  }
}
